import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";

// Components
import FirstScreen from "./first_screen";
import SecondScreen from "./second_screen";
import ThirdScreen from "./third_screen";
import FourthScreen from "./fourth_screen";
import FifthScreen from "./fifth_screen";
import AnnouncementDisplay from "../components/CommonComponents/AnnouncementDisplay";
import EventDisplay from "../components/CommonComponents/EventDisplay";
import ImageDisplay from "../components/CommonComponents/ImageDisplay";
import PrayerOverlayController from "../components/PrayerOverlays/PrayerOverlayController";

// Services
import { getConfiguration } from "../services/configService";
import { getPrayerTimes } from "../services/masjidService";
import { fetchTimeFromCoords } from "../services/timeService";
import { useModuleScheduler } from "../utils/moduleScheduler";

import {
  getOrLoadModule,
  getModuleCached,
  iqamahKey as makeIqamahKey,
  getIqamahReady,
  setIqamahReady,
} from "../shared/cache/moduleDataCache";

import connectUmmahLogo from "../assets/ConnectUmmah.png";

const mapColorSettings = (moduleData) => {
  if (!moduleData) return {};

  return {
    // Header
    headerBackgroundColor: moduleData.header_Background_Color || moduleData.headerBackgroundColor,
    headerTextColor: moduleData.header_Text_Color || moduleData.headerTextColor,
    headerTimingColor: moduleData.header_Timing_Color || moduleData.headerTimingColor,

    // Body
    bodyBackgroundColor: moduleData.body_Background_Color || moduleData.bodyBackgroundColor,
    bodyTextColor: moduleData.body_Text_Color || moduleData.bodyTextColor,
    bodyTimingColor: moduleData.body_Timing_Color || moduleData.bodyTimingColor,

    // Highlights
    highlightColor: moduleData.highlight_Color || moduleData.highlightColor,

    // Footer
    footerBackgroundColor: moduleData.footer_Background_Color || moduleData.footerBackgroundColor,
    footerTextColor: moduleData.footer_Text_Color || moduleData.footerTextColor,
    footerTimingColor: moduleData.footer_Timing_Color || moduleData.footerTimingColor,
  };
};

const LoadingSpinner = ({ text }) => (
  <div className="flex flex-col items-center justify-center h-screen bg-white z-50">
    <div className="relative flex items-center justify-center">
      <div 
        className="rounded-full animate-spin"
        style={{ borderColor: '#f3f4f6', borderTopColor: '#004d40', borderRightColor: '#cfa76e', borderWidth: '0.6svh', width:"20svh", height:"20svh" }}
      ></div>
      <img src={connectUmmahLogo} alt="Connect Ummah" className="absolute h-auto object-contain animate-pulse" style={{width:"15svh"}} />
    </div>
  </div>
);

const ScreenResolver = () => {
  const { publicId } = useParams();

  // ✅ auto reload every 15 minutes (900,000 ms)
  useEffect(() => {
    const RELOAD_MS = 15 * 60 * 1000;

    const id = setInterval(() => {
      // Hard reload the current page
      window.location.reload();
    }, RELOAD_MS);

    return () => clearInterval(id);
  }, []);

  const [config, setConfig] = useState([]);
  const [prayerData, setPrayerData] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [footerAnnouncements, setFooterAnnouncements] = useState([]);

  // 1️⃣ Fetch Configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configRes = await getConfiguration(publicId);
        // console.log("✅ Fetched config:", configRes);
        const modules = configRes?.[0]?.config || [];
        // console.log("📦 Modules extracted:", modules);
        setConfig(modules);
      } catch (err) {
        console.error("❌ Error fetching config:", err);
      }
    };
    fetchConfig();
  }, [publicId]);

  // 2️⃣ Fetch Prayer Data and Timezone
  useEffect(() => {
    const fetchPrayerAndTime = async () => {
      try {
        const businessEntityId = config?.[0]?.business_Entity_Id;
        if (!businessEntityId) return;

        const prayer = await getPrayerTimes(businessEntityId);
        setPrayerData(prayer);

        if (prayer?.startTimes?.latitude && prayer?.startTimes?.longitude) {
          try {
            const timeData = await fetchTimeFromCoords(
              prayer.startTimes.latitude,
              prayer.startTimes.longitude
            );
            const tz = timeData.zoneName || "UTC";
            setTimezone(tz);
            setCurrentTime(moment.tz(timeData.formatted, tz));
          } catch (innerErr) {
            const fallbackTz = moment.tz.guess();
            setTimezone(fallbackTz);
            setCurrentTime(moment.tz(moment(), fallbackTz));
          }
        } else {
          const fallbackTz = moment.tz.guess();
          setTimezone(fallbackTz);
          setCurrentTime(moment.tz(moment(), fallbackTz));
        }
      } catch (err) {
        const fallbackTz = moment.tz.guess();
        setTimezone(fallbackTz);
        setCurrentTime(moment.tz(moment(), fallbackTz));
      }
    };

    if (config && config.length > 0) fetchPrayerAndTime();
  }, [config]);

  // 3️⃣ Keep Current Time Updated
  useEffect(() => {
    if (!timezone) return;
    const interval = setInterval(() => {
      setCurrentTime(moment.tz(moment(), timezone));
    }, 1000);
    return () => clearInterval(interval);
  }, [timezone]);

  // 4️⃣ Scheduler Hook
  const { currentOuter, currentInner, loadModuleData } = useModuleScheduler(config);

  // console.log(currentOuter);

  const activeChildren = useMemo(() => {
    if (!currentOuter || !config) return [];
    return config.filter(m => m.parent_Id === currentOuter.source_Module_Id && m.active);
  }, [currentOuter, config]);

  const iqKey = useMemo(
    () => (currentOuter ? makeIqamahKey(currentOuter, activeChildren) : null),
    [currentOuter, activeChildren]
  );

  const [childrenPreloaded, setChildrenPreloaded] = useState(() =>
    iqKey ? getIqamahReady(iqKey) : false
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!iqKey || childrenPreloaded || !currentOuter) return;
      if (currentOuter.source_Module_Name?.toLowerCase() !== "iqamah_screen") {
          setChildrenPreloaded(true);
          return;
      }
      try {
        const tasks = activeChildren.map((mod) =>
          getOrLoadModule(mod, () => loadModuleData(mod)).catch(() => null)
        );
        await Promise.all(tasks);
        if (!alive) return;
        setChildrenPreloaded(true);
        setIqamahReady(iqKey, true);
      } catch (e) {
        console.warn("⚠️ Preloading failed", e);
      }
    })();
    return () => { alive = false; };
  }, [iqKey, activeChildren, loadModuleData, childrenPreloaded, currentOuter]);

  useEffect(() => {
    if (!currentOuter) {
        setFooterAnnouncements([]);
        return;
    }
    const announcementModule = activeChildren.find(
      child => child.source_Module_Name?.toLowerCase() === "announcements"
    );
    if (!announcementModule) {
        setFooterAnnouncements([]);
        return;
    }
    (async () => {
      try {
        const data = (getModuleCached(announcementModule) ?? (await getOrLoadModule(announcementModule, () => loadModuleData(announcementModule))));
        const results = [];
        if (Array.isArray(data)) {
            data.forEach((item) =>
                results.push({ title: item.title || item.message || "Untitled" })
            );
        }
        setFooterAnnouncements(results);
      } catch (e) {
        setFooterAnnouncements([]);
      }
    })();
  }, [activeChildren, loadModuleData, currentOuter]);

  const hydratedInner = useMemo(() => {
    if (!currentInner) return null;
    const data = currentInner.data ?? getModuleCached(currentInner);
    return data === undefined ? currentInner : { ...currentInner, data };
  }, [currentInner]);

  if (!currentOuter || !prayerData || !timezone || !currentTime) {
    return <LoadingSpinner text="Initializing System" />;
  }

  const moduleName = currentOuter.source_Module_Name?.toLowerCase();
  const screenType = currentOuter?.iqama_Screen_Name;

  // ---------------------------------------------------------------------------
  // ✅ 2. EXTRACT COLORS HERE
  // ---------------------------------------------------------------------------
  const colorSettings = mapColorSettings(currentOuter);

  let content = null;

  if (moduleName === "iqamah_screen") {
    if (!childrenPreloaded) {
        content = <LoadingSpinner text="Syncing Modules" />;
    } else {
        switch (screenType) {
            case "first_screen": 
                content = <FirstScreen 
                    colorSettings={colorSettings}
                    configData={config} publicId={publicId} moduleData={currentOuter}
                    innerModule={hydratedInner} prayerData={prayerData} timezone={timezone}
                    currentTime={currentTime} announcements={footerAnnouncements}
                />; 
                break;
            case "second_screen": 
                content = <SecondScreen 
                    colorSettings={colorSettings}
                    configData={config} publicId={publicId} moduleData={currentOuter}
                    innerModule={hydratedInner} prayerData={prayerData} timezone={timezone}
                    currentTime={currentTime} announcements={footerAnnouncements}
                />; 
                break;
            case "third_screen": 
                content = <ThirdScreen 
                    colorSettings={colorSettings}
                    configData={config} publicId={publicId} moduleData={currentOuter}
                    innerModule={hydratedInner} prayerData={prayerData} timezone={timezone}
                    currentTime={currentTime} announcements={footerAnnouncements}
                />; 
                break;
            case "fourth_screen":
                content = <FourthScreen 
                    colorSettings={colorSettings} 
                    moduleData={currentOuter}
                    prayerData={prayerData} 
                    currentTime={currentTime}
                />; 
                break;
            case "fifth_screen":
                content = <FifthScreen 
                    colorSettings={colorSettings} 
                    moduleData={currentOuter}
                    prayerData={prayerData} 
                    currentTime={currentTime}
                />; 
                break;
            default: 
                content = <div>No valid iqamah screen type found.</div>;
        }
    }
  } 
  else if (moduleName === "announcements" && currentOuter.data?.length > 0) {
      content = <AnnouncementDisplay announcement={currentOuter.data[0]} parent_Id={currentOuter.parent_Id} />;
  }
  else if (moduleName.includes("event") && currentOuter.data?.length > 0) {
      content = <EventDisplay event={currentOuter.data[0]} parent_Id={currentOuter.parent_Id} />;
  }
  else if (moduleName.includes("images") && currentOuter.data?.length > 0) {
      content = <ImageDisplay image={currentOuter.data[0]} parent_Id={currentOuter.parent_Id} />;
  }
  else {
      content = <div className="flex h-screen items-center justify-center text-gray-400">Unknown module: {moduleName}</div>;
  }

  return (
    <div className="relative w-full h-full">
      {prayerData && timezone && (
          <PrayerOverlayController
            iqamahTimes={prayerData.iqamatimes}
            jummahTimes={prayerData.jummahTimes}
            timezone={timezone}
          />
      )}
      <div className="module-transition">
         {content}
      </div>
    </div>
  );
};

export default ScreenResolver;